import { useState, type FormEvent } from 'react';
import { useApiMutation } from '../api/hooks.js';
import {
  toggleMaintenance,
  type MaintenanceToggleResponse,
  type ToggleMaintenanceInput,
} from '../api/endpoints.js';
import { useCan } from '../session/can.js';
import {
  Badge,
  Banner,
  Button,
  Card,
  CardBody,
  CardHeader,
  ErrorState,
  Field,
  Icon,
  Input,
  PageHead,
  Select,
} from '../ui/index.js';
import '../styles/announce.mobile.css';

type Scope = ToggleMaintenanceInput['scope'];

const SCOPES: readonly Scope[] = [
  'none',
  'orders_only',
  'customer_orchestration',
  'full',
];

export function MaintenancePage(): JSX.Element {
  const canToggle = useCan('maintenance:toggle');
  const canView = useCan('maintenance:view');
  const [enabled, setEnabled] = useState(false);
  const [scope, setScope] = useState<Scope>('none');
  const [reason, setReason] = useState('');
  const mutation = useApiMutation<ToggleMaintenanceInput, MaintenanceToggleResponse>(
    (options, input) => toggleMaintenance(options, input),
  );

  const result = mutation.state.kind === 'ok' ? mutation.state.data : null;
  const error = mutation.state.kind === 'error' ? mutation.state : null;

  async function onSubmit(ev: FormEvent): Promise<void> {
    ev.preventDefault();
    if (!canToggle) return;
    await mutation.run({
      enabled,
      scope,
      reason: reason.trim().length > 0 ? reason.trim() : null,
    });
  }

  return (
    <div className="maint-page">
      <PageHead
        crumb={<>Yönetim <Icon.ChevronRight size={11} /> Bakım Modu</>}
        title="Bakım Modu"
        subtitle="API seviyesinde kill-switch. Bu ekran yalnızca durum değiştirir; hangi yüzeylerin kapanacağına sunucu karar verir."
      />

      {!canView ? (
        <ErrorState
          reason="capability_missing:maintenance:view"
          hint="Bu sayfa için yetkiniz yok. DB'de admin_users.role + capabilities alanlarını kontrol edin."
        />
      ) : null}

      {canView ? (
        <Card flush>
          <CardHeader title="Durumu Değiştir" />
          <CardBody>
            <form onSubmit={onSubmit} aria-label="bakım modu kontrol formu">
              <div className="maint-form-grid" style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: '12px 18px', alignItems: 'center' }}>
                <label htmlFor="enabled" className="field-label maint-enabled-label" style={{ margin: 0 }}>Etkin</label>
                <input
                  id="enabled"
                  type="checkbox"
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                  disabled={!canToggle}
                  className="maint-enabled-box"
                  style={{ width: 'auto', justifySelf: 'start' }}
                  aria-label="Etkin"
                />

                <label htmlFor="scope" className="field-label" style={{ margin: 0 }}>Kapsam</label>
                <Select
                  id="scope"
                  value={scope}
                  onChange={(e) => setScope(e.target.value as Scope)}
                  disabled={!canToggle}
                  aria-label="Kapsam"
                  className="maint-scope-select"
                  style={{ maxWidth: 320 }}
                >
                  {SCOPES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </Select>

                <label htmlFor="reason" className="field-label" style={{ margin: 0 }}>Neden</label>
                <Input
                  id="reason"
                  type="text"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="örn. incident-5839: upstream auth sorunu"
                  disabled={!canToggle}
                  aria-label="Neden"
                />
              </div>

              <div className="maint-submit-row" style={{ marginTop: 16 }}>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={!canToggle}
                  loading={mutation.state.kind === 'running'}
                  aria-disabled={!canToggle}
                  title={
                    !canToggle
                      ? 'capability_missing:maintenance:toggle'
                      : 'Bakım modunu uygula'
                  }
                  icon={<Icon.Settings size={14} />}
                >
                  Uygula
                </Button>
                {!canToggle ? (
                  // Touch screens only (shared .touch-detail): why the form is
                  // greyed out; with a mouse it is the button's tooltip.
                  <span className="touch-detail">capability_missing:maintenance:toggle</span>
                ) : null}
              </div>
            </form>

            {result ? (
              <div className="maint-result" style={{ marginTop: 16 }}>
                <Card flush>
                  <CardHeader
                    title="Uygulandı"
                    actions={
                      <Badge tone={result.state.enabled ? 'warn' : 'success'} dot>
                        {result.state.enabled ? 'bakım açık' : 'bakım kapalı'}
                      </Badge>
                    }
                  />
                  <CardBody>
                    <div className="dl-rows">
                      <Row k="Durum" v={result.status} />
                      <Row k="Etkin" v={String(result.state.enabled)} />
                      <Row k="Kapsam" v={<span className="t-mono">{result.state.scope}</span>} />
                      <Row k="Neden" v={result.state.reason ?? '—'} />
                      <Row k="Güncelleme zamanı" v={<span className="t-mono t-xs">{result.state.updatedAt}</span>} />
                    </div>
                    <div style={{ marginTop: 12 }}>
                      <Banner tone="info">
                        <span className="t-mono t-xs maint-corr">correlation: {result.correlationId}</span>
                      </Banner>
                    </div>
                  </CardBody>
                </Card>
              </div>
            ) : null}

            {error ? (
              <div style={{ marginTop: 16 }}>
                <ErrorState
                  reason={error.reason}
                  hint={
                    error.missingCapability
                      ? `Eksik yetki: ${error.missingCapability}`
                      : undefined
                  }
                />
              </div>
            ) : null}
          </CardBody>
        </Card>
      ) : null}
    </div>
  );
}

function Row({ k, v }: { readonly k: string; readonly v: React.ReactNode }): JSX.Element {
  return (
    <div className="dl-row">
      <div className="dl-key">{k}</div>
      <div className="dl-val">{v}</div>
    </div>
  );
}
